/**
 * Supabase Auth Service - Authentication operations
 */

import { supabase, isSupabaseConfigured } from './client';
import type { SignUpData, LoginCredentials, Profile } from '@/types/auth';

export async function signInWithEmail(credentials: LoginCredentials) {
  if (!isSupabaseConfigured) {
    return { data: null, error: { message: 'Auth not configured' } };
  }

  const { data, error } = await supabase.auth.signInWithPassword({
    email: credentials.email,
    password: credentials.password,
  });

  return { data, error };
}

export async function signUpWithEmail(signUpData: SignUpData) {
  if (!isSupabaseConfigured) {
    return { data: null, error: { message: 'Auth not configured' } };
  }

  // First create the auth user
  const { data: authData, error: authError } = await supabase.auth.signUp({
    email: signUpData.email,
    password: signUpData.password,
    options: {
      data: {
        username: signUpData.username,
        user_type: signUpData.user_type,
      },
    },
  });

  if (authError || !authData.user) {
    return { data: null, error: authError };
  }

  // Create the profile
  const profileData: Partial<Profile> = {
    id: authData.user.id,
    user_type: signUpData.user_type,
    username: signUpData.username,
    email: signUpData.email,
    subscription_tier: 'free',
    subscription_status: 'active',
    api_calls_this_month: 0,
    api_calls_reset_date: new Date().toISOString().split('T')[0],
    preferred_currency: 'USD',
  };

  // Add user-type specific fields
  if (signUpData.user_type === 'individual') {
    profileData.first_name = signUpData.first_name;
    profileData.last_name = signUpData.last_name;
    profileData.birth_date = signUpData.birth_date;
    profileData.occupation = signUpData.occupation;
  } else {
    profileData.company_name = signUpData.company_name;
    profileData.contact_person_name = signUpData.contact_person_name;
  }

  const { error: profileError } = await supabase
    .from('profiles')
    .insert([profileData]);

  if (profileError) {
    // Rollback: delete the auth user if profile creation fails
    await supabase.auth.admin?.deleteUser(authData.user.id);
    return { data: null, error: profileError };
  }

  return { data: authData, error: null };
}

export async function signOut() {
  if (!isSupabaseConfigured) {
    return { error: null };
  }
  return await supabase.auth.signOut();
}

export async function resetPassword(email: string) {
  if (!isSupabaseConfigured) {
    return { data: null, error: { message: 'Auth not configured' } };
  }

  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });

  return { data, error };
}

export async function updatePassword(newPassword: string) {
  if (!isSupabaseConfigured) {
    return { data: null, error: { message: 'Auth not configured' } };
  }

  const { data, error } = await supabase.auth.updateUser({
    password: newPassword,
  });

  return { data, error };
}

export async function getSession() {
  if (!isSupabaseConfigured) {
    return { data: { session: null }, error: null };
  }
  return await supabase.auth.getSession();
}

export async function getUser() {
  if (!isSupabaseConfigured) {
    return { data: { user: null }, error: null };
  }
  return await supabase.auth.getUser();
}

export async function fetchProfile(userId: string): Promise<Profile | null> {
  if (!isSupabaseConfigured) {
    return null;
  }

  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (error) {
    console.error('Error fetching profile:', error);
    return null;
  }

  return data as Profile;
}

export async function updateProfile(
  userId: string,
  updates: Partial<Profile>
): Promise<{ error?: string }> {
  if (!isSupabaseConfigured) {
    return { error: 'Auth not configured' };
  }

  const { error } = await supabase
    .from('profiles')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', userId);

  if (error) {
    return { error: error.message };
  }

  return {};
}

export function onAuthStateChange(
  callback: (event: string, session: unknown) => void
) {
  if (!isSupabaseConfigured) {
    return { data: { subscription: { unsubscribe: () => {} } } };
  }
  return supabase.auth.onAuthStateChange(callback);
}
